import { Check } from "lucide-react";
import SectionLogo from "../SectionLogo";
import VideoPlayer from "../VideoPlayer";

const FOUR_EFFECTS = [
  {
    title: "Vasomotor",
    desc: "Vasoconstriction during treatment causes local blood to leave the area carrying away inflammatory waste. Vasodilation immediately after treatment allows blood enriched with oxygen and nutrients to rush to the site.",
  },
  { title: "Analgesic", desc: "Slows nerve signal transmission to decrease pain." },
  { title: "Muscle Relaxant", desc: "Reduces muscle spasms and promotes healing by slowing nerve activity." },
  {
    title: "Anti-Inflammatory",
    desc: "Reduces the temperature and metabolic rate of wounded tissue to cause vasoconstriction leading to decreased blood flow and reducing edema.",
  },
];

const BENEFITS = [
  {
    label: "Wrinkle Reduction:",
    desc: " Relaxes the muscles of the face, reducing wrinkles without the need for injections.",
  },
  {
    label: "Anti-Inflammatory:",
    desc: " Reduces inflammation through cooling the skin and clearing toxins from your blood, additionally decreasing itching and feeling hot. Continued treatments boost the immune response to fight acne-causing bacteria.",
  },
  {
    label: "Antioxidant Effect:",
    desc: " The Subzero positively affects blood flow to promote delivery of antioxidants and other healing materials to the skin to slow aging.",
  },
  {
    label: "Fat Reduction:",
    desc: " Cryotherapy is the perfect tool for body sculpting and fat reduction by safely targeting fat cells. Through the use of cold treatment, cryolipolysis targets specific areas of fat deposits or bulges that do not respond to diet or exercise.",
  },
];

export const WhatIsCryotherapySection = () => {
  return (
    <section className="py-24 bg-[#0a0318] relative overflow-hidden">
      <SectionLogo position="bottom-left" size="medium" opacity={0.06} />
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-cyan-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-4">
              <span className="text-sm text-amber-300 font-medium">The Science</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What is Cryotherapy &
              <span className="block bg-gradient-to-r from-cyan-400 to-amber-400 bg-clip-text text-transparent">
                Why It Works
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <VideoPlayer
                videoUrl="https://www.youtube.com/embed/mchUgggIlPQ"
                title="Cryotherapy Treatment Demo"
                description="See how targeted cryotherapy works"
              />
            </div>
            <div className="order-1 lg:order-2 bg-[#110422]/50 border border-amber-500/20 rounded-3xl p-8 max-h-[600px] overflow-y-auto">
              <p className="text-gray-300 text-lg mb-6">
                The Subzero Cryotherapy device uses pressurized CO&#8322; gas at{" "}
                <span className="text-amber-400 font-semibold">-110°F</span> to target a local area of the body and cause{" "}
                <span className="text-amber-400 font-semibold">&ldquo;thermal shock&rdquo;</span>. The sudden decrease
                in temperature rapidly activates the body&apos;s healing responses via Four Principal Effects:
              </p>

              <div className="space-y-4 mb-8">
                {FOUR_EFFECTS.map((eff) => (
                  <div key={eff.title} className="bg-[#110422]/50 border border-amber-500/20 rounded-xl p-4">
                    <h4 className="text-amber-400 font-bold mb-1">{eff.title}</h4>
                    <p className="text-gray-300 text-sm">{eff.desc}</p>
                  </div>
                ))}
              </div>

              <h3 className="text-xl font-bold text-white mb-4">How It Works</h3>
              <p className="text-gray-300 text-sm mb-4">
                The Subzero uses pressurized CO&#8322; to target a local area and cause thermal shock and{" "}
                <span className="text-amber-400 font-semibold">cryolipolysis</span> — a medical treatment used to
                destroy fat cells by freezing within the temperature range of 24 to 41°F. The cold treatment causes
                apoptosis, or cell death, of subcutaneous fat tissues. Results can be seen immediately, although the
                injured fat cells are gradually eliminated by the body as the cells break down. This can take up to 72
                hours post-treatment. Following a full course of treatment, cells will continue to break down up to 4
                to 6 weeks.
              </p>

              <div className="space-y-3 mb-4">
                {BENEFITS.map((b) => (
                  <div key={b.label} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-amber-400 font-semibold text-sm">{b.label}</span>
                      <span className="text-gray-300 text-sm">{b.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
